import { differenceInMinutes, format } from "date-fns";
import { round } from "lodash-es";

import {
  CSVData,
  CSVExportResultType,
  createSafeFilename,
  formatComment,
  formatDate,
  formatDateISO,
  formatGroupWork,
  formatOutcomes,
  formatPercentage,
  formatPoints,
} from "./file";
import { getUserFullName } from "./user-utils";

import type {
  ClassSessionSummaryType,
  CourseAssessmentSummaryData,
  CourseEnrollmentData,
  CourseGradesData,
  CourseOutcomeSummaryData,
  ExtendedUserEngagementDataType,
  FullAssessmentData,
  PageAssessmentSummaryData,
  StudentOutcomeCompetencyData,
} from "api/api-data-types";
import type { StudentAttendanceMetricType } from "api/api-server-data";
import { getStudentAttendanceMetrics } from "api/api-server-data";
import { calculateRubricScore } from "components/materials/course/assessment/hooks";
import {
  CourseScoreDisplayFormat,
  type OrgUserType,
  type RubricType,
} from "components/server-types";
import { t } from "i18n/i18n";
import type { UserWithCourseUserType } from "store/selectors/types";

export function generateCourseAssessmentSummaryCSV(
  courseData: CourseAssessmentSummaryData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("assessment_summary_report.columns.code"),
      t("assessment_summary_report.columns.course_title"),
      t("assessment_summary_report.columns.instructors"),
      t("assessment_summary_report.columns.last_assessed"),
      t("assessment_summary_report.columns.percent_assessed"),
      t("assessment_summary_report.columns.percent_with_comments"),
      t("assessment_summary_report.columns.comment_length"),
      t("assessment_summary_report.columns.wait_time"),
      t("assessment_summary_report.columns.assessments_per_student"),
    ],

    rows: courseData.map((row) => [
      row.course_code_title,
      row.title,
      row.instructor_names.join(", "),
      formatDate(row.date_last_assessed),
      formatPercentage(row.assessment_percentage),
      formatPercentage(row.comment_percentage),
      row.avg_comment_length,
      row.avg_assessment_delay_days,
      row.avg_student_assessments,
    ]),
  };

  const filename = createSafeFilename("ForumAssessmentSummaryReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateCourseEnrollmentCSV(
  enrollmentData: CourseEnrollmentData[],
  termTitle: string,
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("course_enrollment_report.columns.term"),
      t("course_enrollment_report.columns.course_code"),
      t("course_enrollment_report.columns.course_title"),
      t("course_enrollment_report.columns.role"),
      t("course_enrollment_report.columns.name"),
      t("course_enrollment_report.columns.email"),
      t("course_enrollment_report.columns.disabled_status"),
      t("course_enrollment_report.columns.external_user_id"),
      t("course_enrollment_report.columns.external_course_id"),
      t("course_enrollment_report.columns.invite_status"),
    ],

    rows: enrollmentData.map((row) => [
      row.term,
      row.course_code,
      row.course_title,
      t(`course_enrollment_report.roles.${row.role}`),
      row.name,
      row.email,
      t(`course_enrollment_report.disabled_status_values.${row.disabled_status}`),
      row.external_user_id || "",
      row.external_course_id || "",
      t(`course_enrollment_report.invite_status.${row.invite_status}`),
    ]),
  };

  const filename = `ForumCourseEnrollmentReport-${termTitle.replace(/[^a-zA-Z0-9]/g, "")}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateAssessmentDetailCSV(
  pageData: PageAssessmentSummaryData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("assessment_summary_report.csv_headers.term"),
      t("assessment_summary_report.csv_headers.course"),
      t("assessment_summary_report.csv_headers.instructors"),
      t("assessment_summary_report.csv_headers.page_type"),
      t("assessment_summary_report.csv_headers.page_title"),
      t("assessment_summary_report.csv_headers.assessments_published_on"),
      t("assessment_summary_report.csv_headers.assessment_percentage"),
      t("assessment_summary_report.csv_headers.students_assessed"),
      t("assessment_summary_report.csv_headers.total_students_to_assess"),
      t("assessment_summary_report.csv_headers.latest_responses_collected"),
      t("assessment_summary_report.csv_headers.first_assessment"),
      t("assessment_summary_report.csv_headers.most_recent_assessment"),
      t("assessment_summary_report.csv_headers.comment_percentage"),
      t("assessment_summary_report.csv_headers.avg_comment_length"),
      t("assessment_summary_report.csv_headers.outcomes_assessed"),
    ],

    rows: pageData.map((row) => [
      row.term,
      row.course,
      row.instructors.join(", "),
      t(`assessment_summary_report.page_types.${row.page_type}`),
      row.page_title,
      formatDate(row.assessments_published_on),
      `${row.assessment_percentage}%`,
      row.students_assessed,
      row.total_students_to_assess,
      formatDate(row.latest_responses_collected),
      formatDate(row.first_assessment),
      formatDate(row.most_recent_assessment),
      `${row.comment_percentage}%`,
      row.avg_comment_length,
      formatOutcomes(row.outcomes_assessed),
    ]),
  };

  const filename = createSafeFilename("ForumAssessmentDetailedReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateFullAssessmentCSV(
  assessmentData: FullAssessmentData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  // Sort by student name (case insensitive, alphabetical)
  const sortedData = [...assessmentData].sort((a, b) =>
    a.student_name.toLowerCase().localeCompare(b.student_name.toLowerCase()),
  );

  // TODO: localize these
  // Create headers with outcomes 1-8
  const headers = [
    "Student Name",
    "External ID",
    "Student Email",
    "Term",
    "Course Code",
    "Course Title",
    "Page Type",
    "Page Title",
    "Total Possible Points",
    "Given Points",
    "Group Work",
    "Grader Name",
    "Grader Email",
    "Date Assessment Last Updated",
    "Comment",
  ];

  // Add outcome headers (1-8)
  for (let i = 1; i <= 8; i += 1) {
    headers.push(`Outcome ${i}`);
    headers.push(`Outcome ${i} Score`);
  }

  const rows = sortedData.map((row) => {
    const baseRow = [
      row.student_name,
      row.external_id,
      row.student_email,
      row.term,
      row.course_code,
      row.course_title,
      row.page_type,
      row.page_title,
      row.total_possible_points.toString(),
      formatPoints(row.given_points),
      formatGroupWork(row.group_work),
      row.grader_name,
      row.grader_email,
      formatDateISO(row.date_assessment_last_updated),
      formatComment(row.comment),
    ];

    // Add outcomes (up to 8)
    for (let i = 0; i < 8; i += 1) {
      const outcome = row.outcomes[i];
      if (outcome) {
        baseRow.push(outcome.outcome_name);
        baseRow.push(outcome.outcome_score.toString());
      } else {
        baseRow.push(""); // Empty outcome name
        baseRow.push(""); // Empty outcome score
      }
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const filename = createSafeFilename("ForumFullAssessmentReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateCourseOutcomeSummaryCSV(
  outcomeData: CourseOutcomeSummaryData[],
  termTitle: string,
  outcomeColumns: Array<{ id: string; title: string }>,
): CSVExportResultType {
  // Format outcome titles using localized format
  const outcomeHeaders = outcomeColumns.map((oc) => {
    const formattedTitle = t("common.title_with_average", { title: oc.title });
    return `#${formattedTitle}`;
  });

  // Create headers with localized strings
  const headers = [
    t("student_learning_outcomes_report.columns.student_name"),
    t("student_learning_outcomes_report.columns.student_external_id"),
    t("student_learning_outcomes_report.columns.student_email"),
    t("student_learning_outcomes_report.columns.term"),
    t("student_learning_outcomes_report.columns.course_code"),
    t("student_learning_outcomes_report.columns.course_title"),
    t("student_learning_outcomes_report.columns.course_external_id"),
    ...outcomeHeaders,
  ];

  // Create rows - one row per student-course combination
  const rows = outcomeData.map((row) => {
    const baseRow = [
      row.student_name,
      row.external_id || "",
      row.email,
      row.term_title,
      row.course_code_title,
      row.course_title,
      row.course_external_id || "",
    ];

    // Create a map of outcome_id to avg_score for quick lookup
    const outcomesMap = new Map<string, number>();
    for (const outcome of row.outcomes) {
      outcomesMap.set(outcome.outcome_id, outcome.avg_score);
    }

    // Add outcome values for each outcome column
    for (const outcomeColumn of outcomeColumns) {
      const score = outcomesMap.get(outcomeColumn.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedTerm = termTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumStudentLearningOutcomeReportByCourse-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateStudentOutcomeCompetencyCSV(
  competencyData: StudentOutcomeCompetencyData[],
  termTitle: string,
  outcomeGroups: Array<{ id: string; title: string }>,
  outcomeSubgroups: Array<{ id: string; title: string }>,
  outcomes: Array<{ id: string; title: string }>,
): CSVExportResultType {
  // Format group, subgroup, and outcome titles using localized format
  const groupHeaders = outcomeGroups.map((og) =>
    t("student_learning_outcomes_report.columns.outcome_group_title", { title: og.title }),
  );
  const subgroupHeaders = outcomeSubgroups.map((os) =>
    t("student_learning_outcomes_report.columns.outcome_subgroup_title", { title: os.title }),
  );
  const outcomeHeaders = outcomes.map((oc) => {
    const formattedTitle = t("common.title_with_average", { title: oc.title });
    return `#${formattedTitle}`;
  });

  // Create headers with localized strings
  const headers = [
    t("student_learning_outcomes_report.columns.student_name"),
    t("student_learning_outcomes_report.columns.student_external_id"),
    t("student_learning_outcomes_report.columns.student_email"),
    t("student_learning_outcomes_report.columns.term"),
    ...groupHeaders,
    ...subgroupHeaders,
    ...outcomeHeaders,
  ];

  // Create rows - one row per student
  const rows = competencyData.map((row) => {
    const baseRow = [row.student_name, row.external_id || "", row.email, termTitle];

    // Create maps for quick lookup
    const groupsMap = new Map<string, number>();
    for (const group of row.outcome_groups) {
      groupsMap.set(group.group_id, group.avg_score);
    }

    const subgroupsMap = new Map<string, number>();
    for (const subgroup of row.outcome_subgroups) {
      subgroupsMap.set(subgroup.subgroup_id, subgroup.avg_score);
    }

    const outcomesMap = new Map<string, number>();
    for (const outcome of row.outcomes) {
      outcomesMap.set(outcome.outcome_id, outcome.avg_score);
    }

    // Add group values
    for (const group of outcomeGroups) {
      const score = groupsMap.get(group.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    // Add subgroup values
    for (const subgroup of outcomeSubgroups) {
      const score = subgroupsMap.get(subgroup.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    // Add outcome values
    for (const outcome of outcomes) {
      const score = outcomesMap.get(outcome.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  // Handle "All Terms" for filename - check both English and Spanish
  const sanitizedTerm = termTitle === "" ? "AllTime" : termTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumStudentLearningOutcomeReport-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateCourseGradesCSV(
  gradesData: CourseGradesData[],
  termTitle: string,
  courses: Array<{
    id: string;
    course_grade_prepwork_percentage: number;
    course_grade_activities_percentage: number;
    course_grade_assignments_percentage: number;
  }>,
  courseScoreDisplayFormat?: CourseScoreDisplayFormat,
  orgRubric?: RubricType,
): CSVExportResultType {
  // Create headers with localized strings
  const headers = [
    t("student_course_grades_report.columns.student_name"),
    t("student_course_grades_report.columns.student_external_id"),
    t("student_course_grades_report.columns.student_email"),
    t("student_course_grades_report.columns.term"),
    t("student_course_grades_report.columns.course_code"),
    t("student_course_grades_report.columns.course_title"),
    t("student_course_grades_report.columns.course_external_id"),
    t("assessment_dashboard.student_course_grades.course_score_percent"),
    courseScoreDisplayFormat && courseScoreDisplayFormat !== CourseScoreDisplayFormat.Percent
      ? t(`assessment_dashboard.student_course_grades.course_score_${courseScoreDisplayFormat}`)
      : null,
    t("student_course_grades_report.columns.prep_work_score"),
    t("student_course_grades_report.columns.assignment_score"),
    t("student_course_grades_report.columns.in_class_work_score"),
    t("student_course_grades_report.columns.assessed_pages"),
  ].filter(Boolean) as string[];

  // Create a lookup map for courses by course_id
  const courseLookup = new Map(courses.map((course) => [course.id, course]));

  // Create rows - one row per student-course
  const rows = gradesData.map((row) => {
    // Get multipliers from course lookup
    const course = courseLookup.get(row.course_id);
    const prepMultiplier = course?.course_grade_prepwork_percentage ?? 0;
    const activitiesMultiplier = course?.course_grade_activities_percentage ?? 0;
    const assignmentsMultiplier = course?.course_grade_assignments_percentage ?? 0;

    // NOTE: We need spaces at the beginning to ensure these aren't treated like formulas or dates by excel
    const prepScoreDisplay =
      row.prep_score > 0 || prepMultiplier > 0
        ? ` ${round(row.prep_score, 1).toString()}/${prepMultiplier}`
        : "";
    const activitiesScoreDisplay =
      row.activities_score > 0 || activitiesMultiplier > 0
        ? ` ${round(row.activities_score, 1).toString()}/${activitiesMultiplier}`
        : "";
    const assignmentsScoreDisplay =
      row.assignments_score > 0 || assignmentsMultiplier > 0
        ? ` ${round(row.assignments_score, 1).toString()}/${assignmentsMultiplier}`
        : "";

    const courseScoreForDisplay =
      courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_4 ||
      courseScoreDisplayFormat === CourseScoreDisplayFormat.Scale_5
        ? calculateRubricScore(orgRubric, row.course_score)
        : null;

    return [
      row.student_name,
      row.external_id || "",
      row.email,
      termTitle,
      row.course_code_title,
      row.course_title,
      row.course_external_id || "",
      round(row.course_score, 1).toString(),
      courseScoreDisplayFormat && courseScoreDisplayFormat !== CourseScoreDisplayFormat.Percent
        ? `${courseScoreForDisplay}`
        : null,
      prepScoreDisplay,
      assignmentsScoreDisplay,
      activitiesScoreDisplay,
      row.assessed_pages_count.toString(),
    ].filter(Boolean);
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedTerm = termTitle === "" ? "AllTime" : termTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumStudentCourseGrades-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateSessionLogCSV(
  sessions: ClassSessionSummaryType[],
  courseTitle: string,
  hasVirtualSessions: boolean,
): CSVExportResultType {
  const headers = [
    t("session_log_view.columns.started_at.label"),
    t("session_log_view.columns.duration.label"),
    t("session_log_view.columns.class_type.label"),
    t("session_log_view.columns.attendance.label"),
  ];

  if (hasVirtualSessions) {
    headers.push(
      t("session_log_view.columns.student_talk_time.label"),
      t("session_log_view.columns.instructor_talk_time.label"),
    );
  }

  const rows = sessions.map((session) => {
    const duration = differenceInMinutes(session.end_time, session.start_time);
    const classType = session.view;

    // Attendance percentage
    const attendancePercentage =
      session.students.length > 0
        ? Math.round((session.attending_students.length / session.students.length) * 100)
        : 0;

    const row = [formatDateISO(session.start_time), duration, classType, attendancePercentage];

    if (hasVirtualSessions) {
      // Talk times in minutes
      const studentTalkTime = Math.round((session.student_talk_time_seconds ?? 0) / 60);
      const instructorTalkTime = Math.round((session.instructor_talk_time_seconds ?? 0) / 60);

      row.push(studentTalkTime, instructorTalkTime);
    }

    return row;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedCourseTitle = courseTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumSessionLog-${sanitizedCourseTitle}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateEngagementDataCSV(
  engagementData: ExtendedUserEngagementDataType[],
  courseTitle: string,
): CSVExportResultType {
  const headers = [
    t("engagement_table.columns.student.label"),
    t("engagement_table.columns.effort.label"),
    t("engagement_table.columns.performance.label"),
    t("engagement_table.columns.attendance.label"),
    t("engagement_table.columns.talk_time.label"),
    t("engagement_table.columns.risk_level.label"),
  ];
  const rows = engagementData.map((row) => {
    return [
      getUserFullName(row),
      row.effort_level,
      row.performance_level,
      row.attendance_count ?? null,
      row.avg_talk_time_minutes ?? null,
      row.risk_level,
    ];
  });
  const csvData: CSVData = {
    headers,
    rows,
  };
  const sanitizedCourseTitle = courseTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumEngagementData-${sanitizedCourseTitle}-${new Date().toISOString().split("T")[0]}.csv`;
  return { csvData, filename };
}

export type AttendanceReportRow = {
  studentName: string;
  studentExternalId: string | null;
  studentEmail: string;
  classSessionDate: string;
  classSessionDateTimestamp: number; // Only used for chronological sorting
  timeFirstEntered: string | null;
  minutesInClass: number | null;
  timeLastLeft: string | null;
  sessionStatus: "Present" | "Absent";
  course: string;
};

export function generateAttendanceReportCSV(
  attendanceData: AttendanceReportRow[],
  courseTitle: string,
): CSVExportResultType {
  const headers = [
    "Student Name",
    "Student External Id",
    "Student Email",
    "Class Session Date",
    "Time First Entered",
    "Minutes in Class",
    "Time Last Left",
    "Session Status",
    "Course",
  ];

  const rows = attendanceData.map((row) => [
    row.studentName,
    row.studentExternalId || "",
    row.studentEmail,
    row.classSessionDate,
    row.timeFirstEntered || "",
    row.minutesInClass ?? "",
    row.timeLastLeft || "",
    row.sessionStatus,
    row.course,
  ]);

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedCourseTitle = courseTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumAttendanceReport-${sanitizedCourseTitle}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export async function generateAttendanceReportCSVFromSessions(
  sessions: ClassSessionSummaryType[],
  courseUsers: UserWithCourseUserType[],
  orgUsersMap: Record<string, OrgUserType>,
  courseTitle: string,
  courseId: string,
): Promise<CSVExportResultType> {
  // Fetch attendance metrics
  const attendanceMetrics = await getStudentAttendanceMetrics(courseId);

  // Create a map of user_id to user info
  const userMap = new Map(
    courseUsers.map((cu) => {
      const orgUser = orgUsersMap[cu.user_id];
      return [
        cu.user_id,
        {
          name: getUserFullName(cu),
          email: cu.email,
          externalId: orgUser?.external_user_id ?? null,
        },
      ];
    }),
  );

  // Create a map of session_id to session info
  const sessionMap = new Map(
    sessions.map((session) => [
      session.class_session_id,
      {
        startTime: session.start_time,
        endTime: session.end_time,
        duration: differenceInMinutes(session.end_time, session.start_time),
        classType: session.view === "virtual-class" ? "Virtual Class" : "In-Person Class",
      },
    ]),
  );

  // Create a map of (user_id, session_id) to attendance metrics
  const attendanceMetricsMap = new Map<string, StudentAttendanceMetricType>();
  for (const metric of attendanceMetrics) {
    const key = `${metric.user_id}:${metric.class_session_id}`;
    attendanceMetricsMap.set(key, metric);
  }

  // Generate attendance report rows
  const reportRows: AttendanceReportRow[] = [];
  for (const session of sessions) {
    const sessionInfo = sessionMap.get(session.class_session_id);
    if (sessionInfo) {
      // Get all students enrolled in the course
      for (const userId of session.students) {
        const userInfo = userMap.get(userId);
        if (userInfo) {
          const attendanceKey = `${userId}:${session.class_session_id}`;
          const attendanceMetric = attendanceMetricsMap.get(attendanceKey);
          const presenceMinutes = attendanceMetric?.presence_minutes ?? null;
          const sessionDurationMinutes = sessionInfo.duration;
          const halfDuration = sessionDurationMinutes / 2;
          const sessionStatus =
            presenceMinutes !== null && presenceMinutes >= halfDuration ? "Present" : "Absent";

          const classSessionDate = format(new Date(sessionInfo.startTime), "MM-dd-yyyy");
          const classSessionDateTimestamp = new Date(sessionInfo.startTime).getTime();
          const timeFirstEntered = attendanceMetric?.first_seen
            ? format(new Date(attendanceMetric.first_seen), "HH:mm:ss")
            : null;
          const timeLastLeft = attendanceMetric?.last_seen
            ? format(new Date(attendanceMetric.last_seen), "HH:mm:ss")
            : null;

          reportRows.push({
            studentName: userInfo.name,
            studentExternalId: userInfo.externalId,
            studentEmail: userInfo.email,
            classSessionDate,
            classSessionDateTimestamp,
            timeFirstEntered,
            minutesInClass: presenceMinutes,
            timeLastLeft,
            sessionStatus,
            course: courseTitle || "Course",
          });
        }
      }
    }
  }

  // Sort by student name, then by session date (chronologically)
  reportRows.sort((a, b) => {
    const nameCompare = a.studentName.localeCompare(b.studentName);
    if (nameCompare !== 0) return nameCompare;
    return a.classSessionDateTimestamp - b.classSessionDateTimestamp;
  });

  return generateAttendanceReportCSV(reportRows, courseTitle || "Course");
}
